import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { billingApi } from '../../services/api';
import { usePortalStore, toast } from '../../stores';
import { NexusCard, NexusButton, NexusBadge } from '../../components/nexus-ui';
import { CreditCard, Check, Lightning, Crown, Rocket, Star, Receipt, ChartBar, Warning } from '@phosphor-icons/react';
import BillingAlertsPanel from '../../components/instrument/BillingAlertsPanel';

const PLAN_ICONS = {
  free: Lightning,
  starter: Rocket,
  professional: Star,
  enterprise: Crown,
};

const PLAN_COLORS = {
  free: 'border-nexus-border-emphasized',
  starter: 'border-blue-500/30',
  professional: 'border-nexus-accent-mint/30',
  enterprise: 'border-nexus-accent-violet/30',
};

const PortalBilling = () => {
  const queryClient = useQueryClient();
  const { tenant, setAuth, token, user } = usePortalStore();
  const [processingPlan, setProcessingPlan] = useState(null);

  const { data: plans } = useQuery({
    queryKey: ['billingPlans'],
    queryFn: () => billingApi.getPlans().then(r => r.data),
  });

  const { data: billing } = useQuery({
    queryKey: ['currentBilling'],
    queryFn: () => billingApi.getCurrentBilling().then(r => r.data),
    refetchInterval: 30000, // Refresh every 30s for real-time metering
  });

  const upgradeMutation = useMutation({
    mutationFn: async (planTier) => {
      setProcessingPlan(planTier);
      if (!billing?.razorpay_enabled) {
        const res = await billingApi.activateDemoPlan({ plan_tier: planTier });
        return res.data;
      }
      const orderRes = await billingApi.createOrder({ plan_tier: planTier });
      const order = orderRes.data;
      if (order.demo_mode) {
        const res = await billingApi.activateDemoPlan({ plan_tier: planTier });
        return res.data;
      }
      return new Promise((resolve, reject) => {
        const options = {
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          name: 'Cognara',
          description: `Upgrade to ${planTier} plan`,
          order_id: order.razorpay_order_id,
          handler: async (response) => {
            try {
              const verifyRes = await billingApi.verifyPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              resolve(verifyRes.data);
            } catch (err) { reject(err); }
          },
          prefill: { email: tenant?.billing_email || user?.email, name: user?.name },
          theme: { color: '#1E4E8C' },
          modal: { ondismiss: () => reject(new Error('Payment cancelled')) },
        };
        if (window.Razorpay) {
          const rzp = new window.Razorpay(options);
          rzp.open();
        } else { reject(new Error('Razorpay SDK not loaded')); }
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['currentBilling'] });
      queryClient.invalidateQueries({ queryKey: ['portalStats'] });
      if (data.plan_tier) {
        const updatedTenant = { ...tenant, plan_tier: data.plan_tier };
        setAuth(token, user, updatedTenant);
      }
      toast.success(data.message || 'Plan upgraded successfully!');
      setProcessingPlan(null);
    },
    onError: (err) => { toast.error(err.message || 'Payment failed'); setProcessingPlan(null); },
  });

  const currentPlan = tenant?.plan_tier || 'free';

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-[1600px] mx-auto" data-testid="portal-billing">
      {/* Header */}
      <div>
        <h2 className="font-heading text-xl font-bold text-nexus-text-primary">Billing & Metering</h2>
        <p className="text-sm text-nexus-text-secondary mt-1">Manage your subscription, track usage, and view invoices</p>
      </div>

      {/* Billing Alerts Panel */}
      <BillingAlertsPanel />

      {/* Usage Meter + Current Charges — side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Real-time Usage Meter */}
        <NexusCard variant="glass" hover={false} className="p-5">
          <h3 className="font-heading text-sm font-semibold text-nexus-text-primary mb-4 flex items-center gap-2" data-testid="usage-meter-title">
            <ChartBar className="w-4 h-4 text-nexus-accent-mint" weight="fill" />
            Token Usage (This Month)
          </h3>
          {billing?.quota ? (
            <div>
              {/* Progress bar */}
              <div className="relative h-3 bg-nexus-bg-tertiary/40 rounded-full overflow-hidden mb-3">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(billing.quota.utilization, 100)}%` }}
                  transition={{ duration: 1, ease: 'easeOut' }}
                  className={`h-full rounded-full ${
                    billing.quota.utilization >= 90 ? 'bg-red-500' :
                    billing.quota.utilization >= 80 ? 'bg-amber-500' :
                    'bg-nexus-accent-mint'
                  }`}
                  data-testid="usage-progress-bar"
                />
              </div>
              <div className="flex justify-between text-xs text-nexus-text-tertiary mb-4">
                <span>{billing.quota.consumed.toLocaleString()} tokens used</span>
                <span>{billing.quota.limit.toLocaleString()} limit</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <MeterStat label="Input" value={billing.usage?.input_tokens || 0} />
                <MeterStat label="Output" value={billing.usage?.output_tokens || 0} />
                <MeterStat label="API Calls" value={billing.usage?.api_calls || 0} />
              </div>
              {billing.quota.is_overage && (
                <div className="mt-3 flex items-center gap-2 text-xs text-amber-400 bg-amber-500/10 rounded-lg px-3 py-2" data-testid="overage-warning">
                  <Warning className="w-4 h-4" weight="fill" />
                  <span>{billing.quota.overage_tokens.toLocaleString()} overage tokens — billed at Rs. {billing.overage_rate}/1K tokens</span>
                </div>
              )}
              {billing.quota.utilization >= 80 && !billing.quota.is_overage && (
                <div className="mt-3 flex items-center gap-2 text-xs text-amber-400 bg-amber-500/10 rounded-lg px-3 py-2" data-testid="quota-warning">
                  <Warning className="w-4 h-4" weight="fill" />
                  <span>{billing.quota.utilization}% of token quota used. Consider upgrading.</span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-nexus-text-tertiary">Loading usage data...</div>
          )}
        </NexusCard>

        {/* Current Charges */}
        <NexusCard variant="glass" hover={false} className="p-5">
          <h3 className="font-heading text-sm font-semibold text-nexus-text-primary mb-4 flex items-center gap-2" data-testid="charges-title">
            <Receipt className="w-4 h-4 text-nexus-accent-violet" weight="fill" />
            Current Month Charges
          </h3>
          {billing?.current_charges ? (
            <div className="space-y-3">
              <ChargeRow label="Base Plan Fee" value={billing.current_charges.base_fee_inr} />
              <ChargeRow label="Overage Fee" value={billing.current_charges.overage_fee_inr} />
              <ChargeRow label="GST (18%)" value={billing.current_charges.gst_inr} dim />
              <div className="border-t border-nexus-border-subtle pt-3">
                <ChargeRow label="Total Estimated" value={billing.current_charges.total_inr} bold />
              </div>
              {!billing?.razorpay_enabled && (
                <NexusBadge variant="warning" className="mt-2">Demo Mode — No Real Charges</NexusBadge>
              )}
            </div>
          ) : (
            <div className="text-sm text-nexus-text-tertiary">Loading charges...</div>
          )}
        </NexusCard>
      </div>

      {/* Current Plan Banner */}
      <NexusCard variant="glass" hover={false} className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-nexus flex items-center justify-center">
              {(() => {
                const Icon = PLAN_ICONS[currentPlan] || Lightning;
                return <Icon className="w-5 h-5 text-nexus-bg-primary" weight="fill" />;
              })()}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-base font-semibold text-nexus-text-primary">
                  Current Plan: {billing?.plan_label || currentPlan}
                </h3>
                <NexusBadge variant="success" data-testid="current-plan-badge">Active</NexusBadge>
              </div>
              <p className="text-xs text-nexus-text-secondary">
                {billing?.price_inr > 0 ? `Rs. ${billing.price_inr}/month` : 'Free forever'} 
                {billing?.token_limit ? ` — ${(billing.token_limit / 1000).toLocaleString()}K tokens/mo` : ''}
                {billing?.overage_rate > 0 ? ` — Rs. ${billing.overage_rate}/1K overage` : ''}
              </p>
            </div>
          </div>
        </div>
      </NexusCard>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {plans?.map((plan, i) => {
          const isCurrent = plan.tier === currentPlan;
          const Icon = PLAN_ICONS[plan.tier] || Lightning;
          const isPopular = plan.tier === 'professional';

          return (
            <motion.div key={plan.tier} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
              <NexusCard
                variant="glass" hover={!isCurrent}
                className={`p-5 relative ${PLAN_COLORS[plan.tier]} ${isCurrent ? 'ring-2 ring-nexus-accent-mint' : ''}`}
                data-testid={`plan-card-${plan.tier}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <NexusBadge variant="premium">Most Popular</NexusBadge>
                  </div>
                )}
                <div className="text-center mb-5">
                  <div className={`w-10 h-10 rounded-xl mx-auto flex items-center justify-center mb-2.5 ${isCurrent ? 'bg-nexus-accent-mint/20' : 'bg-white/5'}`}>
                    <Icon className={`w-5 h-5 ${isCurrent ? 'text-nexus-accent-mint' : 'text-nexus-text-secondary'}`} weight="duotone" />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-nexus-text-primary">{plan.label}</h3>
                  <div className="mt-1.5">
                    {plan.price_inr > 0 ? (
                      <div>
                        <span className="text-2xl font-bold text-nexus-text-primary">Rs. {plan.price_inr.toLocaleString('en-IN')}</span>
                        <span className="text-nexus-text-tertiary text-xs">/mo</span>
                      </div>
                    ) : (
                      <span className="text-2xl font-bold text-nexus-text-primary">Free</span>
                    )}
                  </div>
                  {plan.token_limit > 0 && (
                    <p className="text-[11px] text-nexus-text-tertiary mt-1">{(plan.token_limit / 1000).toLocaleString()}K tokens/mo</p>
                  )}
                  {plan.overage_rate > 0 && (
                    <p className="text-[10px] text-nexus-text-tertiary">+ Rs. {plan.overage_rate}/1K overage</p>
                  )}
                </div>
                <ul className="space-y-2 mb-5">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-xs text-nexus-text-secondary">
                      <Check className="w-3.5 h-3.5 text-nexus-accent-mint flex-shrink-0" weight="bold" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <NexusButton
                  className="w-full"
                  variant={isCurrent ? 'ghost' : isPopular ? 'primary' : 'secondary'}
                  disabled={isCurrent || processingPlan !== null}
                  isLoading={processingPlan === plan.tier}
                  onClick={() => upgradeMutation.mutate(plan.tier)}
                  data-testid={`upgrade-${plan.tier}-btn`}
                >
                  {isCurrent ? 'Current Plan' : plan.price_inr === 0 ? 'Downgrade' : 'Upgrade'}
                </NexusButton>
              </NexusCard>
            </motion.div>
          );
        })}
      </div>

      {/* Billing History / Invoices */}
      {billing?.billing_history?.length > 0 && (
        <NexusCard variant="glass" hover={false}>
          <div className="p-5">
            <h3 className="font-heading text-sm font-semibold text-nexus-text-primary mb-4 flex items-center gap-2">
              <Receipt className="w-4 h-4" weight="duotone" />
              Invoice History
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-nexus-text-tertiary border-b border-nexus-border-subtle">
                    <th className="text-left pb-2 font-medium">Date</th>
                    <th className="text-left pb-2 font-medium">Plan</th>
                    <th className="text-right pb-2 font-medium">Tokens</th>
                    <th className="text-right pb-2 font-medium">Base</th>
                    <th className="text-right pb-2 font-medium">Overage</th>
                    <th className="text-right pb-2 font-medium">GST</th>
                    <th className="text-right pb-2 font-medium">Total</th>
                    <th className="text-right pb-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {billing.billing_history.map((record) => (
                    <tr key={record.id} className="border-b border-white/[0.03]" data-testid={`invoice-${record.id}`}>
                      <td className="py-2.5 text-nexus-text-secondary">{new Date(record.created_at).toLocaleDateString()}</td>
                      <td className="py-2.5 text-nexus-text-primary capitalize">{record.plan_tier}</td>
                      <td className="py-2.5 text-right text-nexus-text-secondary">{(record.tokens_consumed || 0).toLocaleString()}</td>
                      <td className="py-2.5 text-right text-nexus-text-secondary">Rs. {(record.base_fee_inr || record.total_inr || 0).toLocaleString('en-IN')}</td>
                      <td className="py-2.5 text-right text-nexus-text-secondary">Rs. {(record.token_usage_fee_inr || 0).toLocaleString('en-IN')}</td>
                      <td className="py-2.5 text-right text-nexus-text-secondary">Rs. {(record.gst_inr || 0).toLocaleString('en-IN')}</td>
                      <td className="py-2.5 text-right text-nexus-text-primary font-medium">Rs. {(record.total_inr || 0).toLocaleString('en-IN')}</td>
                      <td className="py-2.5 text-right">
                        <NexusBadge
                          variant={record.payment_status === 'paid' ? 'success' : record.payment_status === 'demo' ? 'info' : record.payment_status === 'failed' ? 'error' : 'warning'}
                        >
                          {record.payment_status}
                        </NexusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </NexusCard>
      )}
    </div>
  );
};

const MeterStat = ({ label, value }) => (
  <div className="text-center p-2 bg-nexus-bg-tertiary/20 rounded-lg">
    <p className="text-base font-semibold text-nexus-text-primary">{value.toLocaleString()}</p>
    <p className="text-[10px] text-nexus-text-tertiary">{label}</p>
  </div>
);

const ChargeRow = ({ label, value, bold, dim }) => (
  <div className="flex justify-between items-center">
    <span className={`text-xs ${dim ? 'text-nexus-text-tertiary' : 'text-nexus-text-secondary'}`}>{label}</span>
    <span className={`text-xs ${bold ? 'font-semibold text-nexus-text-primary text-sm' : dim ? 'text-nexus-text-tertiary' : 'text-nexus-text-primary'}`}>
      Rs. {value?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>
  </div>
);

export default PortalBilling;
